import { useEffect, useMemo, useRef, useState } from "react";
import { useAutoscrollOnResize } from "./useAutoscrollOnResize";
import { useCountUp } from "./useCountUp";

export const usePreviewFlow = (args: {
  previewOpen: boolean;
  setPreviewOpen: (b: boolean) => void;
  productosLength: number;
  resumen: {
    subtotal: number;
    impuestosFederales: number;
    impuestosLocales: number;
    total: number;
  };
  moneda: string;
  metodoPago: string;
  diasCredito: number | "";
}) => {
  const [showAll, setShowAll] = useState(false);
  const maxPreview = 2;
  const hayMas = args.productosLength > maxPreview;

  // scroll y “leer hasta el final”
  const [hasSeenEnd, setHasSeenEnd] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  useAutoscrollOnResize(
    contentRef as React.RefObject<HTMLElement>,
    args.previewOpen
  );

  useEffect(() => {
    if (args.previewOpen) setHasSeenEnd(false);
  }, [args.previewOpen]);

  const handleScrollPreview = () => {
    const el = contentRef.current;
    if (!el) return;
    const threshold = 24;
    const reachedBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (reachedBottom) setHasSeenEnd(true);
  };

  const scrollToEnd = () =>
    contentRef.current?.scrollTo({
      top: contentRef.current.scrollHeight,
      behavior: "smooth",
    });

  // step/secciones
  const [step, setStep] = useState(0);
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (args.previewOpen) {
      setStep(0);
      setHasSeenEnd(false);
    }
  }, [args.previewOpen]);
  const goNext = () => setStep((s) => s + 1);
  const scrollToSection = (idx: number) =>
    sectionRefs.current[idx]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  // countups
  const cuSubtotal = useCountUp(args.resumen.subtotal, 700, [
    step === 5,
    args.resumen.subtotal,
    args.moneda,
  ]);
  const cuImpFed = useCountUp(args.resumen.impuestosFederales, 700, [
    step === 5,
    args.resumen.impuestosFederales,
    args.moneda,
  ]);
  const cuImpLoc = useCountUp(args.resumen.impuestosLocales, 700, [
    step === 5,
    args.resumen.impuestosLocales,
    args.moneda,
  ]);
  const cuTotal = useCountUp(args.resumen.total, 900, [
    step === 5,
    args.resumen.total,
    args.moneda,
  ]);

  // auto-scroll al final tras animación
  const [autoScrolledResumen, setAutoScrolledResumen] = useState(false);
  useEffect(() => {
    if (args.previewOpen && step === 5) setAutoScrolledResumen(false);
  }, [args.previewOpen, step]);

  useEffect(() => {
    if (
      args.previewOpen &&
      step === 5 &&
      cuSubtotal.done &&
      cuImpFed.done &&
      cuImpLoc.done &&
      cuTotal.done &&
      !autoScrolledResumen
    ) {
      setAutoScrolledResumen(true);
      requestAnimationFrame(() =>
        endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
      );
    }
  }, [
    args.previewOpen,
    step,
    cuSubtotal.done,
    cuImpFed.done,
    cuImpLoc.done,
    cuTotal.done,
    autoScrolledResumen,
  ]);

  // pequeño “pegador” al fondo mientras estás en el paso 5
  useEffect(() => {
    if (!(args.previewOpen && step === 5)) return;
    const id = setInterval(() => {
      const el = contentRef.current;
      if (!el) return;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
      if (!atBottom) el.scrollTop = el.scrollHeight;
    }, 120);
    return () => clearInterval(id);
  }, [args.previewOpen, step]);

  // validación confirmar
  const confirmarDisabledBase = useMemo(
    () =>
      args.metodoPago === "PPD" &&
      (!args.diasCredito ||
        !Number.isFinite(Number(args.diasCredito)) ||
        Number(args.diasCredito) <= 0),
    [args.metodoPago, args.diasCredito]
  );
  const confirmarDisabled = confirmarDisabledBase || !hasSeenEnd;

  return {
    // preview list
    showAll,
    setShowAll,
    maxPreview,
    hayMas,
    // scrolling
    hasSeenEnd,
    contentRef,
    endRef,
    handleScrollPreview,
    scrollToEnd,
    // steps
    step,
    setStep,
    goNext,
    sectionRefs,
    scrollToSection,
    // counters
    cuSubtotal,
    cuImpFed,
    cuImpLoc,
    cuTotal,
    confirmarDisabledBase,
    confirmarDisabled,
  };
};
